import { getPrimaryUserInput, normalizeFinnishPromptText, normalizeUserInputToEnglish, sanitizeEnglishPrompt } from "@/lib/languageNormalizer";
import { appendFinnishVisualGuidance } from "@/lib/visualPromptDefaults";

const OTHER_VALUE = "Muu, mikä?";

const rawValue = (form, key) => form[key];

const stringify = (input) => {
  if (Array.isArray(input)) return input.filter(Boolean).join(", ");
  return (input || "").trim();
};

const value = (form, key) => stringify(form[key]);

const sentence = (condition, text) => (condition ? text : null);

const list = (items) => items.filter(Boolean).map((item) => `- ${item}`).join("\n");

const fieldValue = (form, field) => {
  const current = rawValue(form, field.id);
  const other = value(form, `${field.id}__other`);
  if (Array.isArray(current)) {
    const cleaned = current.filter((item) => item && item !== OTHER_VALUE);
    return [...cleaned, other].filter(Boolean).join(", ");
  }
  if (current === OTHER_VALUE) return other;
  return stringify(current);
};

const templateValue = (form, template, key) => {
  const field = template.lomakekentat.find((item) => item.id === key);
  return field ? fieldValue(form, field) : value(form, key);
};

const formLines = (form, template) =>
  template.lomakekentat
    .filter((field) => field.id !== "aiTyokalu")
    .map((field) => {
      const current = fieldValue(form, field);
      return current ? `${field.label}: ${current}` : null;
    })
    .filter(Boolean)
    .join("\n");

const joinNatural = (items) => items.filter(Boolean).join(", ");

const isYesChoice = (input) => String(input || "").toLowerCase().includes("kyllä");

const aspectValue = (input) => String(input || "").match(/\d+\s*:\s*\d+/)?.[0].replace(/\s/g, "") || String(input || "").trim();

const cleanEnglishText = (input) =>
  sanitizeEnglishPrompt(String(input || "")
    .replace(/\s+/g, " ")
    .replace(/\s+([,.!?;:])/g, "$1")
    .replace(/\bhighly realistic realistic\b/gi, "highly realistic")
    .replace(/\brealistic realistic\b/gi, "realistic")
    .replace(/\bCreate a (\d+) seconds highly realistic\b/gi, "Create a highly realistic $1-second")
    .replace(/\bCreate a (\d+) second highly realistic\b/gi, "Create a highly realistic $1-second")
    .replace(/\bUse image light\b/gi, "Use the lighting conditions from the reference image")
    .replace(/\bimage light\b/gi, "the lighting conditions from the reference image")
    .replace(/\bDo not change Henkilön kasvot\b/gi, "Do not change the person's face")
    .replace(/\bHenkilön kasvot\b/gi, "the person's face")
    .replace(/\bMake it feel like an iphone photo feel\b/gi, "Make it feel like an authentic iPhone photo")
    .replace(/\bMake it feel like an iPhone photo feel\b/gi, "Make it feel like an authentic iPhone photo")
    .replace(/\bUse natural colors for the color mood\b/gi, "Use natural colors and a realistic color mood")
    .replace(/\bhighly realistic, natural-looking a natural lifestyle image\b/gi, "highly realistic, natural-looking lifestyle image")
    .replace(/\bhighly realistic, natural-looking an? ([a-z-]+(?: [a-z-]+){0,4} image)\b/gi, "highly realistic, natural-looking $1")
    .replace(/\bwoman looks helsingin tuomiokirkkoa\b/gi, "the woman looks at Helsinki Cathedral")
    .replace(/\bwoman looks tunturimaisemaa\b/gi, "the woman looking out over a Finnish fell landscape")
    .replace(/\bwoman looks järvimaisemaa\b/gi, "the woman looking at a Finnish lakeside landscape")
    .replace(/\bwoman looks metsämaisemaa\b/gi, "the woman looking at a Finnish forest landscape")
    .replace(/\bwoman looks merimaisemaa\b/gi, "the woman looking at the sea view")
    .replace(/\bwoman looks kameraan\b/gi, "the woman looking at the camera")
    .replace(/\bwoman looks auringonlaskua\b/gi, "the woman looking at the sunset")
    .replace(/\blooks helsingin tuomiokirkkoa\b/gi, "looks at Helsinki Cathedral")
    .replace(/\bthe person from the attached reference image looks at Helsinki Cathedral with an amazed, captivated expression on their face\b/gi, "the person from the attached reference image looking at Helsinki Cathedral with an amazed, captivated expression on their face")
    .replace(/\bThe environment is a natural realistic detail\. ?/gi, "")
    .replace(/\bThe environment is a realistic real-life environment\. ?/gi, "")
    .replace(/\bThe environment is the user-provided detail expressed in natural English\. ?/gi, "")
    .replace(/\bthe user-provided detail expressed in natural English\b/gi, "provided detail")
    .replace(/\bwoman grilling sausages over a campfire istuen on a fallen tree\b/gi, "a woman sitting on a fallen tree and grilling sausages over a campfire")
    .replace(/\ba woman grilling sausages over a campfire sitting on a fallen tree\b/gi, "a woman sitting on a fallen tree and grilling sausages over a campfire")
    .replace(/\ba a\b/gi, "a")
    .replace(/\ban a\b/gi, "an")
    .trim());

const isMeaningfulDetail = (input) => {
  const value = String(input || "").trim().toLowerCase();
  return Boolean(value) && !["a natural realistic detail", "provided detail", "a realistic real-life environment", "the selected content format"].includes(value);
};

const genericMovementPattern = /^(a\s+)?(subtle natural movement|natural movement|small movement|slight movement|calm movement|small believable movement|subtle movement|gentle movement|natural and calm movement)$/i;

const isGenericMovement = (input) => genericMovementPattern.test(String(input || "").trim());

const stripGenericActorPrefix = (input) =>
  String(input || "")
    .replace(/^(a|the)\s+(person|woman|man|subject|content creator)\s+(is\s+)?/i, "")
    .replace(/^person\s+/i, "")
    .trim();

const combineSubjectAndAction = (subject, action) => {
  const subjectText = cleanEnglishText(subject);
  const actionText = cleanEnglishText(action);
  if (!subjectText) return actionText;
  if (!actionText || subjectText.toLowerCase().includes(actionText.toLowerCase())) return subjectText;
  const actionWithoutActor = stripGenericActorPrefix(actionText);
  if (!actionWithoutActor) return subjectText;
  return cleanEnglishText(`${subjectText} ${actionWithoutActor}`);
};

const concreteNaturalMovement = (startText = "", environmentText = "") => {
  const context = `${startText} ${environmentText}`.toLowerCase();
  if (context.includes("lake") || context.includes("lakeside") || context.includes("dock") || context.includes("summer landscape")) {
    return "the person gently turns their head toward the lake, breathes naturally, and the hair or clothing moves slightly in the summer breeze";
  }
  if (context.includes("beach") || context.includes("sunset")) {
    return "the person gently turns their head toward the horizon, breathes naturally, and the hair or clothing moves slightly in the sea breeze";
  }
  if (context.includes("forest") || context.includes("campfire")) {
    return "the person gently shifts their posture, breathes naturally, and makes a small realistic hand or head movement while the environment stays stable";
  }
  return "the person makes a subtle natural movement, such as gently turning their head, breathing naturally, and letting the hair or clothing move slightly";
};

const aiTool = (form, template) => templateValue(form, template, "aiTyokalu");

const naturalDetailPhrase = (label, current) => {
  const normalized = label.toLowerCase();
  if (normalized.includes("vält")) return `Vältä näitä asioita: ${current}.`;
  if (normalized.includes("cta")) return `Toimintakehotus on ${current}.`;
  if (normalized.includes("kuinka") || normalized.includes("montako")) return `${label}: ${current}.`;
  return `${label} on ${current}.`;
};

const naturalDetails = (form, template) =>
  template.lomakekentat
    .filter((field) => field.id !== "aiTyokalu")
    .map((field) => {
      const current = fieldValue(form, field);
      return current ? naturalDetailPhrase(field.label, current) : null;
    })
    .filter(Boolean)
    .join(" ");

const naturalInstructionText = (items) =>
  items
    .filter(Boolean)
    .flatMap((item) => String(item).split(","))
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => `${item.charAt(0).toUpperCase()}${item.slice(1).replace(/[.!?]$/, "")}.`)
    .join(" ");

const textToolGuidance = (tool) => {
  const selected = String(tool || "").toLowerCase();
  if (!tool) return "";
  if (selected.includes("chatgpt") || selected.includes("claude")) {
    return `Kirjoita prompti rakenteellisena tekstipromptina: rooli, tavoite, konteksti, tyyli, formaatti, rajoitteet ja lopputulos. Pyydä mallia tekemään maltilliset oletukset vain silloin, kun tieto puuttuu, ja kieltämään geneerinen, liian tekoälymäinen ilmaisu.`;
  }
  if (selected.includes("gemini")) {
    return `Sovita prompti Geminille rakenteellisena tekstipromptina: rooli, tavoite, konteksti, tyyli, formaatti, rajoitteet ja lopputulos. Anna selkeä konteksti ensin, määrittele haluttu lopputulos tarkasti ja pyydä vastausta, jossa on konkreettiset esimerkit, luonnollinen suomen kieli ja helposti muokattava rakenne.`;
  }
  if (selected.includes("suno")) {
    return `Tee lopputulos Sunoa varten kahteen erilliseen osaan. Style-kenttään tulee genre, tunnelma, tempo, instrumentit, rakenne ja tuotannollinen tuntuma. Lyrics-kenttään tulee vain lauluteksti tai lyhyt ohje "instrumental, no vocals", jos käyttäjä haluaa instrumentaalin.`;
  }
  if (selected.includes("elevenlabs")) {
    return `Sovita prompti ElevenLabsille äänen tai voiceoverin suunnitteluun: määrittele äänen ikävaikutelma, energia, puhenopeus, tauot, tunne, ääntämisen selkeys ja mahdollinen äänimaisema. Korosta luonnollista puhetta, ei robottimaista lukemista.`;
  }
  if (selected.includes("heygen")) {
    return `Sovita prompti Heygen-avatarille: tee käsikirjoituksesta suoraan puhuttava, lisää luonnolliset tauot, selkeä rytmi, huulisynkkaa tukeva lauserakenne ja hillitty, uskottava esiintymistyyli.`;
  }
  return `Sovita prompti työkalun ${tool} käyttölogiikkaan: pidä ohje selkeänä, vältä turhia täytesanoja ja lisää vain sellaiset tekniset rajoitteet, jotka auttavat saamaan luonnollisen, käyttövalmiin lopputuloksen.`;
};

const visualTranslations = {
  "Realistinen henkilökuva": "a realistic portrait",
  "Luonnollinen lifestyle-kuva": "a natural lifestyle image",
  "UGC-tyylinen tuotekuva": "a UGC-style product image",
  Brändikuva: "a brand image",
  "Somepostauksen kuva": "a social media post image",
  "Instagram-karusellin kuva": "an Instagram carousel image",
  "LinkedIn-henkinen kuva": "a LinkedIn-style image",
  "Behind the scenes -kuva": "a behind-the-scenes image",
  Matkakuva: "a travel image",
  "Ruoka- tai kahvilakuva": "a food or café image",
  "Hyvinvointi- tai spa-kuva": "a wellness or spa image",
  "Tuotekuva aidossa ympäristössä": "a product image in a real environment",
  "Skandinaavinen koti": "a Scandinavian home",
  Keittiö: "a kitchen",
  Olohuone: "a living room",
  "Työpiste / kotitoimisto": "a home office workspace",
  Työpiste: "a workspace",
  Kahvila: "a café",
  Kaupunkikatu: "an urban street",
  Luonto: "nature",
  Ranta: "a beach",
  Metsä: "a forest",
  Hotelli: "a hotel",
  "Spa / sauna / hyvinvointitila": "a spa, sauna or wellness space",
  "Spa / sauna": "a spa or sauna",
  Studio: "a studio",
  Myymälä: "a store",
  Kylpyhuone: "a bathroom",
  "Auto / matkustustilanne": "a car or travel situation",
  Tapahtuma: "an event",
  "iPhone-kuvan tuntu": "an iPhone photo feel",
  "Luonnollinen somekuva": "a natural social media photo",
  "UGC-tyylinen kuva": "a UGC-style photo",
  "Dokumentaarinen valokuva": "documentary photography",
  "Lifestyle-valokuva": "lifestyle photography",
  "Editorial-tyylinen kuva": "an editorial-style photo",
  "Ammattimainen mutta luonnollinen kuva": "a professional but natural photo",
  "Analoginen filmikuva": "an analog film photo",
  "Pehmeä luonnonvalokuva": "a soft natural-light photo",
  "Käsivaralta otetun kuvan tuntu": "a handheld photo feel",
  "Silmien tasolta": "eye-level framing",
  "Hieman yläviistosta": "a slightly high angle",
  "Hieman alaviistosta": "a slightly low angle",
  Lähikuva: "a close-up",
  Puolikuva: "a medium shot",
  Kokovartalokuva: "a full-body shot",
  "Over the shoulder": "an over-the-shoulder angle",
  Sivuprofiili: "a side profile",
  "45 asteen kulma": "a 45-degree angle",
  "Flat lay": "a flat lay",
  "POV-kuva": "a POV shot",
  "Peilin kautta": "a mirror shot",
  "Tiukka rajaus": "tight framing",
  "Pää ja hartiat": "head-and-shoulders framing",
  Puolivartalokuva: "a half-body frame",
  "Laajempi ympäristö mukana": "wider framing with the environment visible",
  "Tuote lähikuvassa": "a close-up of the product",
  "Henkilö ja ympäristö tasapainossa": "balanced framing between the person and environment",
  "Luonnonvalo ikkunasta": "soft natural window light",
  "Pehmeä aamuvalo": "soft morning light",
  "Kultaisen tunnin valo": "golden hour light",
  "Pilvisen päivän pehmeä valo": "soft overcast daylight",
  "Sisätilan lämmin valo": "warm indoor light",
  "Lämmin sisävalo": "warm indoor light",
  "Hämärä iltavalo": "dim evening light",
  "Kameran salamavalo": "camera flash",
  "Studiohenkinen pehmeä valo": "soft studio-style light",
  "Pehmeä studiovalo": "soft studio light",
  Vastavalo: "backlight",
  "Sivulta tuleva valo": "side light",
  Luonnollinen: "natural",
  Aito: "authentic",
  Rauhallinen: "calm",
  Lämmin: "warm",
  Inspiroiva: "inspiring",
  Arkinen: "everyday and casual",
  Raikas: "fresh",
  Tyylikäs: "stylish",
  Minimalistinen: "minimalistic",
  Luova: "creative",
  Energinen: "energetic",
  Intensiivinen: "intense",
  Herkkä: "sensitive",
  Luotettava: "trustworthy",
  "Premium mutta luonnollinen": "premium but natural",
  "Realistinen iho": "realistic skin",
  "Luonnollinen ihon tekstuuri": "natural skin texture",
  "Ei muovista ihoa": "no plastic skin",
  "Ei beauty filter -lookia": "no beauty filter look",
  "Luonnolliset kädet": "natural hands",
  "Realistiset sormet": "realistic fingers",
  "Uskottavat mittasuhteet": "believable proportions",
  "Aidot kasvonpiirteet": "authentic facial features",
  "Hienovaraiset epätäydellisyydet": "subtle human imperfections",
  "Luonnolliset hiukset": "natural hair",
  "Aito vaatetuksen tekstuuri": "real clothing texture",
  "Realistinen valo ja varjot": "realistic light and shadows",
  "Ei stock photo -fiilistä": "no stock photo feeling",
  "Ei liian täydellinen sommittelu": "not overly perfect composition",
  "Dokumentaarinen aitous": "documentary authenticity",
  "Luonnolliset värit": "natural colors",
  "Lämmin sävymaailma": "a warm color palette",
  "Viileä sävymaailma": "a cool color palette",
  "Skandinaavinen minimalismi": "Scandinavian minimalism",
  "Pehmeät neutraalit sävyt": "soft neutral tones",
  "Kontrastinen mutta realistinen": "contrasty but realistic colors",
  "Vintage / filmimäinen": "a vintage film-like look",
  "Raikas ja valoisa": "fresh and bright colors",
  "Tumma ja tunnelmallinen": "dark and atmospheric colors",
  "Realistinen image-to-video": "realistic image-to-video",
  "Text-to-video": "text-to-video",
  "UGC-tyylinen videoklippi": "a UGC-style video clip",
  "B-roll-video": "a B-roll video",
  "Reels-klippi": "a Reels clip",
  Mainosvideo: "an ad video",
  "Lifestyle-video": "a lifestyle video",
  "Tuotevideo aidossa ympäristössä": "a product video in a real environment",
  Henkilövideo: "a person-focused video",
  Matkavideo: "a travel video",
  "Behind the scenes -video": "a behind-the-scenes video",
  "Hidas push-in": "a slow push-in",
  "Hidas pull-back": "a slow pull-back",
  "Kevyt handheld-liike": "subtle handheld movement",
  "Staattinen kamera": "a static camera",
  "Hidas sivuttaisliike": "a slow side movement",
  "Pehmeä tracking shot": "a smooth tracking shot",
  "Kevyt zoom-in": "a subtle zoom-in",
  "POV-kameraliike": "POV camera movement",
  "Over the shoulder -liike": "over-the-shoulder movement",
  "Dokumentaarinen käsivarakuvaus": "documentary handheld filming",
  "Drone-tyylinen liike": "drone-style movement",
  "Ei kameran liikettä": "no camera movement",
  "Pieni luonnollinen pään liike": "small natural head movement",
  "Katseen siirtyminen": "a subtle gaze shift",
  "Kevyt hymy": "a slight smile",
  "Käden luonnollinen liike": "natural hand movement",
  "Hidas kävely": "slow walking",
  "Tuotteen nostaminen käteen": "picking up the product",
  "Hiusten tai vaatteen kevyt liike": "subtle hair or clothing movement",
  "Luonnollinen hengitys": "natural breathing",
  "Pieni asennon muutos": "a small posture change",
  "Arkinen toiminta": "an everyday action",
  "Ei suurta liikettä": "no major movement",
  "Luonnollinen ja rauhallinen": "natural and calm",
  "Kevyesti hymyilevä": "slightly smiling",
  Keskittynyt: "focused",
  Inspiroitunut: "inspired",
  Utelias: "curious",
  Rentoutunut: "relaxed",
  "Iloinen mutta ei poseeraava": "happy but not posed",
  Mietteliäs: "thoughtful",
  Luottavainen: "confident",
  "Aito ja arkinen": "authentic and everyday",
  "Hidas ja pehmeä": "slow and soft",
  "Luonnollinen arjen tempo": "natural everyday tempo",
  "Nopea someklippi": "fast social media clip pacing",
  "3 sekuntia": "3 seconds",
  "5 sekuntia": "5 seconds",
  "8 sekuntia": "8 seconds",
  "10 sekuntia": "10 seconds",
  "15 sekuntia": "15 seconds",
  "Säilytä henkilön identiteetti": "keep the person's identity stable",
  "Säilytä kasvonpiirteet": "preserve facial features",
  "Säilytä hiustyyli": "preserve hairstyle",
  "Säilytä vaatetus": "preserve clothing",
  "Säilytä tuotteen ulkonäkö": "preserve product appearance",
  "Säilytä ympäristö": "preserve the environment",
  "Luonnollinen kasvojen liike": "natural facial movement",
  "Luonnollinen kehon liike": "natural body movement",
  "Realistiset kädet": "realistic hands",
  "Realistinen valo": "realistic light",
  "Uskottavat varjot": "believable shadows",
  "Ei flickeröintiä": "no flickering",
  "Ei kasvojen vääristymistä": "no face distortion",
  "Ei ylimääräisiä raajoja": "no extra limbs",
  "Ei muovista AI-lookia": "no plastic AI look",
};

const translateVisual = (input) =>
  String(input || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => visualTranslations[item] || item)
    .join(", ");

const englishValue = (input, context = "generic") => normalizeUserInputToEnglish(translateVisual(input), context);

const visualEnglishToFinnish = {
  "no plastic skin": "ei muovista ihoa",
  "no overly smooth skin": "ei ylisiloteltua ihoa",
  "no beauty filter": "ei beauty filter -ilmettä",
  "no AI-looking face": "ei tekoälymäisiä kasvoja",
  "no distorted hands": "ei vääristyneitä käsiä",
  "no warped hands": "ei vääntyneitä käsiä",
  "no extra fingers": "ei ylimääräisiä sormia",
  "no extra limbs": "ei ylimääräisiä raajoja",
  "no warped body": "ei vääristynyttä kehoa",
  "no unrealistic eyes": "ei epärealistisia silmiä",
  "no fake smile": "ei teennäistä hymyä",
  "no overprocessed lighting": "ei ylikäsiteltyä valaistusta",
  "no generic stock photo look": "ei geneeristä stock photo -ilmettä",
  "no overly perfect composition": "ei liian täydellistä sommittelua",
  "no unnatural skin texture": "ei epäluonnollista ihon tekstuuria",
  "no unrealistic proportions": "ei epärealistisia mittasuhteita",
  "do not change identity": "älä muuta identiteettiä",
  "do not change facial features": "älä muuta kasvonpiirteitä",
  "do not change hairstyle": "älä muuta hiustyyliä",
  "do not change clothing": "älä muuta vaatetusta",
  "avoid flicker": "vältä flickeröintiä",
  "avoid morphing": "vältä morphingia",
  "avoid extra limbs": "vältä ylimääräisiä raajoja",
  "avoid identity changes": "vältä identiteetin muuttumista",
  "avoid unrealistic motion": "vältä epärealistista liikettä",
  "no face distortion": "ei kasvojen vääristymistä",
  "no flickering": "ei flickeröintiä",
  "no unnatural movement": "ei epäluonnollista liikettä",
  "no robotic movement": "ei robottimaista liikettä",
  "no over-smoothed AI look": "ei ylisiloteltua AI-ilmettä",
  "no unrealistic camera movement": "ei epärealistista kameraliikettä",
  "no melting objects": "ei sulavia tai muuttuvia esineitä",
  "no changing background": "ei muuttuvaa taustaa",
  "no changing product shape": "ei tuotteen muodon muuttumista",
  "no exaggerated expressions": "ei ylinäyteltyjä ilmeitä",
};

const finnishVisualValue = (input) =>
  String(input || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) => visualEnglishToFinnish[item] || item)
    .map(normalizeFinnishPromptText)
    .join(", ");

const lowercaseFirst = (input) => (/^(AI|UGC|B-roll|Reels|Text-to-video)/.test(String(input || "")) ? String(input || "") : String(input || "").replace(/^./, (char) => char.toLowerCase()));

const articleFreeEnglishValue = (input, context = "generic") =>
  englishValue(input, context)
    .replace(/^a\s+/i, "")
    .replace(/^an\s+/i, "")
    .trim();

const formatVideoDuration = (input) => {
  const normalized = englishValue(input).toLowerCase();
  const number = normalized.match(/\d+/)?.[0];
  return number ? `${number}-second` : "short";
};

const formatVideoType = (input) => {
  const normalized = englishValue(input || "AI video", "contentType")
    .toLowerCase()
    .replace(/^a\s+/, "")
    .replace(/^an\s+/, "")
    .replace(/^realistic\s+/, "")
    .replace(/^highly realistic\s+/, "");
  if (normalized.includes("image-to-video")) return "image-to-video clip";
  if (normalized.includes("text-to-video")) return "text-to-video clip";
  if (normalized.includes("video clip") || normalized.includes("ai video")) return normalized;
  if (normalized.includes("video")) return normalized;
  return `${normalized} video clip`;
};

const naturalLightingSentence = (lighting, start, environment) => {
  const combined = `${lighting} ${start} ${environment}`.toLowerCase();
  if (lighting) {
    const value = englishValue(lighting, "lighting").toLowerCase();
    if (value.includes("lighting conditions from the reference image")) return "Use the lighting conditions from the reference image.";
    if (isMeaningfulDetail(value)) return `Use ${value}.`;
  }
  if (combined.includes("campfire") || combined.includes("nuot")) return "Use natural low-light evening conditions with warm firelight illuminating the subject and believable shadows in the surrounding forest environment.";
  if (combined.includes("forest") || combined.includes("mets")) return "Use natural outdoor light with believable shadows and realistic depth in the environment.";
  return "Use realistic, consistent natural lighting with credible shadows.";
};

const refinedVideoAction = (action, start, subjectMove, expression) => {
  const actionText = cleanEnglishText(englishValue(action, "action"));
  const startText = cleanEnglishText(englishValue(start, "subject"));
  const movementText = cleanEnglishText(englishValue(subjectMove, "action"));
  const expressionText = cleanEnglishText(englishValue(expression, "tone"));
  if (isGenericMovement(actionText)) return concreteNaturalMovement(startText);
  if (actionText && actionText !== startText && !startText.includes(actionText)) return actionText;
  if ((startText.includes("campfire") || actionText.includes("campfire")) && (startText.includes("sausage") || actionText.includes("sausage"))) return "she gently turns the sausages over the fire and gives a soft natural smile toward the camera";
  if (isGenericMovement(movementText)) return concreteNaturalMovement(startText);
  const combinedMovement = [movementText, expressionText].filter(isMeaningfulDetail).join(" with ");
  return combinedMovement || concreteNaturalMovement(startText);
};

const refinedVideoToolInstruction = (tool, aspect, attachedImage) => {
  const selected = String(tool || "").toLowerCase();
  const ratio = aspectValue(aspect);
  const reference = isYesChoice(attachedImage);
  if (selected.includes("kling")) return `Optimize for Kling AI as a ${reference ? "controlled image-to-video" : "physically believable video"} prompt with stable identity, subtle natural motion, no flicker, no morphing and no distorted hands.${ratio ? ` Keep the ${ratio} format.` : ""}`;
  if (selected.includes("veo")) return "Optimize for Google Veo with cinematic but realistic camera movement, clear temporal continuity, physically plausible action and consistent lighting.";
  if (selected.includes("runway")) return "Optimize for Runway with one precise shot, one main action, one camera movement and restrained motion so faces, hands and background details stay stable.";
  if (selected.includes("higgsfield")) return "Optimize for Higgsfield with a natural social-media-style lifestyle feel while keeping movement controlled and realistic.";
  return videoToolInstruction(tool, aspect, attachedImage, "en");
};

const avoidSentence = (items) => {
  const cleaned = items
    .filter(Boolean)
    .flatMap((item) => String(item).split(","))
    .map((item) => item.trim())
    .filter(Boolean)
    .map((item) =>
      item
        .replace(/^avoid\s+/i, "")
        .replace(/^no\s+/i, "")
        .replace(/^do not change\s+/i, "changes to ")
        .replace(/^do not\s+/i, "")
    );
  return cleaned.length ? `Avoid ${cleaned.join(", ")}.` : "";
};

const aspectRatioSentences = {
  image: {
    "Pysty 9:16 — TikTok, Reels, Shorts, Stories": {
      en: "Use a vertical 9:16 aspect ratio suitable for TikTok, Reels, Shorts and Stories.",
      fi: "Käytä pystysuuntaista 9:16-kuvasuhdetta, joka sopii TikTokiin, Reelsiin, Shortseihin ja Storiesiin.",
    },
    "Neliö 1:1 — Instagram-postaus": {
      en: "Use a square 1:1 composition suitable for an Instagram post.",
      fi: "Käytä neliömäistä 1:1-kuvasuhdetta, joka sopii Instagram-postaukseen.",
    },
    "Pysty 4:5 — Instagram-feed": {
      en: "Use a 4:5 vertical composition suitable for Instagram feed.",
      fi: "Käytä pystysuuntaista 4:5-kuvasuhdetta, joka sopii Instagram-feediin.",
    },
    "Vaaka 16:9 — YouTube, esitykset, verkkosivut": {
      en: "Use a 16:9 horizontal composition suitable for YouTube, presentations and websites.",
      fi: "Käytä vaakasuuntaista 16:9-kuvasuhdetta, joka sopii YouTubeen, esityksiin ja verkkosivuille.",
    },
    "Vaaka 3:2 — luonnollinen valokuvamainen kuva": {
      en: "Use a 3:2 horizontal composition for a natural photographic feel.",
      fi: "Käytä vaakasuuntaista 3:2-kuvasuhdetta luonnollisen valokuvamaisen tunteen luomiseksi.",
    },
    "Vaaka 4:3 — perinteinen kuvaformaatti": {
      en: "Use a 4:3 horizontal composition for a classic photo format.",
      fi: "Käytä vaakasuuntaista 4:3-kuvasuhdetta perinteiseen kuvaformaattiin.",
    },
    "Muotokuva 2:3 — henkilökuvat ja editorial-kuvat": {
      en: "Use a 2:3 portrait composition suitable for portraits and editorial images.",
      fi: "Käytä muotokuvamaista 2:3-kuvasuhdetta henkilökuviin ja editorial-kuviin.",
    },
    "Muotokuva 3:4 — some- ja lifestyle-kuvat": {
      en: "Use a 3:4 portrait composition suitable for social media and lifestyle images.",
      fi: "Käytä muotokuvamaista 3:4-kuvasuhdetta some- ja lifestyle-kuviin.",
    },
    "Banneri 21:9 — hero-kuvat, verkkosivut": {
      en: "Use a wide 21:9 banner composition suitable for hero images and websites.",
      fi: "Käytä leveää 21:9-bannerikuvasuhdetta hero-kuviin ja verkkosivuille.",
    },
  },
  video: {
    "Pysty 9:16 — TikTok, Reels, Shorts": {
      en: "Create the video in a vertical 9:16 format suitable for Reels, TikTok and Shorts.",
      fi: "Tee video pystysuuntaiseen 9:16-formaattiin, joka sopii Reelsiin, TikTokiin ja Shortseihin.",
    },
    "Neliö 1:1 — somevideo": {
      en: "Use a square 1:1 format suitable for social media video.",
      fi: "Käytä neliömäistä 1:1-formaattia, joka sopii somevideoon.",
    },
    "Pysty 4:5 — Instagram-feed-video": {
      en: "Use a 4:5 vertical format suitable for Instagram feed video.",
      fi: "Käytä pystysuuntaista 4:5-formaattia, joka sopii Instagram-feed-videoon.",
    },
    "Vaaka 16:9 — YouTube, verkkosivut, esitykset": {
      en: "Use a 16:9 horizontal format suitable for YouTube, websites and presentations.",
      fi: "Käytä vaakasuuntaista 16:9-formaattia, joka sopii YouTubeen, verkkosivuille ja esityksiin.",
    },
    "Vaaka 4:3 — perinteinen videoformaatti": {
      en: "Use a 4:3 horizontal format for a classic video frame.",
      fi: "Käytä vaakasuuntaista 4:3-formaattia perinteiseen videoruutuun.",
    },
    "Elokuvamainen 21:9 — cinematic-video": {
      en: "Use a cinematic 21:9 widescreen format.",
      fi: "Käytä elokuvamaista 21:9-laajakuvaformaattia.",
    },
  },
};

const aspectSentence = (aspect, type, language) => {
  if (!aspect) return "";
  const preset = aspectRatioSentences[type]?.[aspect];
  if (preset) return preset[language];
  if (language === "fi") return `Käytä kuvasuhdetta ${aspect}.`;
  return type === "video" ? `Create the video in a ${aspect} format.` : `Use a ${aspect} aspect ratio.`;
};

const attachedImageSentence = (choice, type, language) => {
  if (!choice) return "";
  const lower = choice.toLowerCase();
  if (lower.includes("kyllä")) {
    if (language === "fi") return type === "video" ? "Käytä liitettyä lähtökuvaa videon pohjana ja säilytä sen tärkeät yksityiskohdat mahdollisimman hyvin." : "Käytä liitettyä kuvaa referenssinä ja säilytä sen olennaiset piirteet mahdollisimman luonnollisesti.";
    return type === "video" ? "Use the attached starting image as the basis for the video and preserve its key details as naturally as possible." : "Use the attached image as a reference and preserve its essential details as naturally as possible.";
  }
  if (lower.includes("ei")) {
    if (language === "fi") return type === "video" ? "Luo video tekstiohjeen perusteella ilman liitettyä lähtökuvaa." : "Luo kuva tekstiohjeen perusteella ilman liitettyä referenssikuvaa.";
    return type === "video" ? "Create the video from the text prompt without an attached starting image." : "Create the image from the text prompt without an attached reference image.";
  }
  return language === "fi" ? "Jos käytät liitettyä kuvaa, säilytä sen tärkeät yksityiskohdat; muuten tee maltilliset oletukset tekstiohjeen perusteella." : "If an attached image is used, preserve its key details; otherwise make reasonable assumptions from the text prompt.";
};

const imageToolInstruction = (tool, aspect, attachedImage, language) => {
  const selected = String(tool || "").toLowerCase();
  if (!tool) return "";
  const ratio = aspectValue(aspect);
  const reference = isYesChoice(attachedImage);
  if (selected.includes("midjourney")) {
    return language === "fi"
      ? `Optimoi Midjourneylle: käytä tiiviitä visuaalisia avainsanoja, selkeää kameratyyliä, valaistusta, sommittelua, realistista materiaalituntua ja fotorealistista tyyliä.${ratio ? ` Lisää kuvasuhde --ar ${ratio}.` : ""} Vältä pitkiä keskustelumaisia ohjeita.`
      : `Optimize for Midjourney: use compact visual keywords, clear camera style, lighting, composition, realistic material detail and a photorealistic style direction.${ratio ? ` Add --ar ${ratio}.` : ""} Avoid long conversational instructions.`;
  }
  if (selected.includes("nano") || selected.includes("gemini")) {
    return language === "fi"
      ? `Optimoi Gemini / Nano Banana -kuvalle: pidä ohje selkeänä ja kuvanmuokkausta tukevana, painota luonnollista identiteetin, tuotteen ja ympäristön säilymistä${reference ? " lähtökuvasta" : ""}, realistista valoa ja ei-muovista lopputulosta.`
      : `Optimize for Gemini / Nano Banana: keep the instruction clear and edit-friendly, emphasize preserving natural identity, product and environment consistency${reference ? " from the reference image" : ""}, realistic light and a non-plastic result.`;
  }
  if (selected.includes("gpt image") || selected.includes("chatgpt image")) {
    return language === "fi"
      ? `Optimoi ChatGPT Image / GPT Image -työkaluun: kirjoita ohje selkeänä kuvaus- tai muokkauspyyntönä, kerro mitä säilytetään, mitä muutetaan ja millainen realistinen lopputulos halutaan. Älä käytä pelkkiä tyylitägejä.`
      : `Optimize for ChatGPT Image / GPT Image: write this as a clear generation or edit request, state what to preserve, what to change and what realistic final result is expected. Do not rely only on style tags.`;
  }
  return language === "fi"
    ? `Sovita ohje valittuun kuvatyökaluun ${tool}: painota realistista tyyliä, selkeää sommittelua, valoa, kuvasuhdetta ja luonnollisia yksityiskohtia.`
    : `Adapt the instruction for ${tool}: emphasize realistic style, clear composition, lighting, aspect ratio and natural details.`;
};

const videoToolInstruction = (tool, aspect, attachedImage, language) => {
  const selected = String(tool || "").toLowerCase();
  if (!tool) return "";
  const ratio = aspectValue(aspect);
  const reference = isYesChoice(attachedImage);
  if (selected.includes("kling")) {
    return language === "fi"
      ? `Optimoi Kling AI:lle: ${reference ? "käsittele tämä image-to-video-promptina, jossa lähtökuvan identiteetti, kasvot, vaatetus, tuote ja tausta säilyvät" : "pidä kohtaus fyysisesti uskottavana ja jatkuvuudeltaan vakaana"}. Käytä pientä luonnollista liikettä, vältä kasvojen vääristymistä, flickeröintiä, morphingia, ylimääräisiä sormia ja epärealistisia kamerahyppyjä.${ratio ? ` Säilytä formaatti ${ratio}.` : ""}`
      : `Optimize for Kling AI: ${reference ? "treat this as an image-to-video prompt where the starting image identity, face, clothing, product and background stay consistent" : "keep the scene physically believable and continuity-safe"}. Use subtle natural motion, avoid face distortion, flicker, morphing, extra fingers and unrealistic camera jumps.${ratio ? ` Keep the ${ratio} format.` : ""}`;
  }
  if (selected.includes("veo")) {
    return language === "fi"
      ? `Optimoi Google Veolle: kirjoita elokuvallinen mutta realistinen kohtaus, jossa on selkeä kameran liike, fyysisesti mahdollinen toiminta, jatkuva valaistus ja ajallinen jatkuvuus. Vältä äkillisiä leikkauksia, epäselvää liikettä ja ylinäyteltyjä ilmeitä.`
      : `Optimize for Google Veo: write a cinematic but realistic scene with clear camera movement, physically plausible action, consistent lighting and temporal continuity. Avoid sudden cuts, vague motion and exaggerated expressions.`;
  }
  if (selected.includes("runway")) {
    return language === "fi"
      ? `Optimoi Runwaylle: anna tarkka shot-kuvaus, kesto, yksi pääliike, yksi kameraliike ja selkeät jatkuvuusrajat. Pidä liike hillittynä, jotta video ei ala sulattaa kasvoja, käsiä, taustaa tai tuotetta.`
      : `Optimize for Runway: give a precise shot description, duration, one main action, one camera movement and clear continuity limits. Keep motion restrained so the video does not melt faces, hands, background or product details.`;
  }
  if (selected.includes("higgsfield")) {
    return language === "fi"
      ? `Optimoi Higgsfieldille: painota sosiaalisen median tyylistä kameraliikettä, hallittua energiaa, fashion- tai lifestyle-henkistä ilmettä ja identiteetin säilymistä. Pidä kasvojen ja kehon liike luonnollisena, ei ylikoreografioituna.`
      : `Optimize for Higgsfield: emphasize social-media-style camera movement, controlled energy, a fashion or lifestyle feel and identity preservation. Keep face and body movement natural, not over-choreographed.`;
  }
  if (selected.includes("heygen")) {
    return language === "fi"
      ? `Optimoi Heygenille: tee tästä avatarille tai puhuvalle videolle sopiva ohje. Pidä puhe selkeänä, lisää luonnolliset tauot, vältä liian pitkiä virkkeitä ja ohjaa ilme rauhalliseksi sekä uskottavaksi.`
      : `Optimize for Heygen: adapt this for an avatar or talking video. Keep speech clear, add natural pauses, avoid overly long sentences and guide the expression to stay calm and believable.`;
  }
  return language === "fi"
    ? `Sovita ohje valittuun videotyökaluun ${tool}: painota luonnollista liikettä, kameran jatkuvuutta, realistista valoa, identiteetin säilymistä ja virheiden kuten flickeröinnin sekä kasvojen vääristymisen välttämistä.`
    : `Adapt the instruction for ${tool}: emphasize natural motion, camera continuity, realistic light, identity preservation and avoiding errors such as flicker and face distortion.`;
};

export const createEmptyForm = (template) =>
  template.lomakekentat.reduce((acc, field) => {
    acc[field.id] = field.defaultValue || (field.type === "multiselect" ? [] : "");
    acc[`${field.id}__other`] = "";
    return acc;
  }, {});

export const buildTemplateSkeleton = (template) => {
  const fields = template.lomakekentat.map((field) => `- ${field.label}`).join("\n");
  const structure = Array.isArray(template.promptirunko.rakenne) ? template.promptirunko.rakenne.map((item) => `- ${item}`).join("\n") : template.promptirunko.rakenne;

  return `Nimi: ${template.nimi}
Kategoria: ${template.kategoria}

Mihin tämä auttaa:
${template.kayttotarkoitus}

Täytettävät tiedot:
${fields}

Valmiin promptin rakenne:
Rooli: ${template.promptirunko.rooli}

Rakenne:
${structure}

Formaatti:
${template.promptirunko.formaatti}

Lopputuloksen kieli: ${template.lopputuloksenKieli}

Realismi- ja luonnollisuusohjeet:
${list(template.realismiJaLuonnollisuusohjeet)}

Negatiiviset ohjeet:
${list(template.negatiivisetOhjeet)}`;
};

const buildDirectImagePrompt = (form, template) => {
  const tool = aiTool(form, template);
  const primaryInput = getPrimaryUserInput(form, ["kohde", "tilanne", "ymparisto", "vaatetus", "lukitut"], true);
  const subject = templateValue(form, template, "kohde") || primaryInput;
  const imageType = templateValue(form, template, "kuvaustyyppi");
  const environment = templateValue(form, template, "ymparisto");
  const action = templateValue(form, template, "tilanne");
  const camera = templateValue(form, template, "kameratyyli");
  const aspect = templateValue(form, template, "kuvasuhde");
  const attachedImage = templateValue(form, template, "liitettyKuva");
  const angle = templateValue(form, template, "kuvakulma");
  const framing = templateValue(form, template, "rajaus");
  const light = templateValue(form, template, "valaistus");
  const mood = templateValue(form, template, "tunnelma");
  const realism = templateValue(form, template, "realismi");
  const clothing = templateValue(form, template, "vaatetus");
  const colors = templateValue(form, template, "varit");
  const locked = templateValue(form, template, "lukitut");
  const negative = joinNatural([...template.negatiivisetOhjeet, templateValue(form, template, "negatiiviset")]);

  const scene = [finnishVisualValue(subject) || "pääkohde", finnishVisualValue(action)].filter(Boolean).join(" ");
  const prompt = [
    `Luo erittäin realistinen, luonnollisen näköinen ${lowercaseFirst(finnishVisualValue(imageType) || "AI-kuva")}, jossa ${scene}${environment ? ` ympäristössä: ${finnishVisualValue(environment)}` : ""}.`,
    aspectSentence(aspect, "image", "fi"),
    attachedImageSentence(attachedImage, "image", "fi"),
    `Kuvan tulee tuntua aidolta ${camera ? finnishVisualValue(camera) : "iPhone-kuvalta"}, ei liian asetellulta tai ylisilotellulta mainoskuvalta.`,
    `Käytä ${light ? finnishVisualValue(light) : "pehmeää luonnonvaloa ikkunasta"}.`,
    angle || framing ? `Rajaus ja kuvakulma: ${finnishVisualValue(joinNatural([angle, framing]))}.` : "Pidä henkilö, tuote ja ympäristö tasapainoisesti näkyvissä.",
    mood ? `Tunnelma: ${finnishVisualValue(mood)}.` : "Tunnelman tulee olla luonnollinen, rauhallinen ja uskottava.",
    clothing ? `Vaatetus ja ulkoasu: ${finnishVisualValue(clothing)}.` : "Pidä vaatetus ja ulkoasu luonnollisina tilanteeseen nähden.",
    colors ? `Sävymaailma: ${finnishVisualValue(colors)}.` : "Sävymaailman tulee olla luonnollinen ja realistinen.",
    `Painota realistista ihon tekstuuria, luonnollisia käsiä, uskottavia sormia, aitoja mittasuhteita, luonnollista valoa ja todentuntuisia yksityiskohtia.${realism ? ` Huomioi lisäksi: ${finnishVisualValue(realism)}.` : ""}`,
    locked ? `Älä muuta: ${finnishVisualValue(locked)}.` : "",
    tool ? imageToolInstruction(tool, aspect, attachedImage, "fi") : "",
    `Vältä ${finnishVisualValue(negative) || "muovista ihoa, ylisiloteltua AI-ilmettä, vääristyneitä käsiä, ylimääräisiä sormia ja epäluonnollisia mittasuhteita"}.`,
  ].filter(Boolean).join(" ");

  return appendFinnishVisualGuidance(prompt);
};

const buildDirectVideoPrompt = (form, template) => {
  const tool = aiTool(form, template);
  const videoType = templateValue(form, template, "videotyyppi");
  const start = templateValue(form, template, "aloitus");
  const action = templateValue(form, template, "tapahtuma");
  const camera = templateValue(form, template, "kamera");
  const aspect = templateValue(form, template, "kuvasuhde");
  const attachedImage = templateValue(form, template, "liitettyKuva");
  const subjectMove = templateValue(form, template, "kohteenLiike");
  const expression = templateValue(form, template, "ilme");
  const environment = templateValue(form, template, "ymparisto");
  const lighting = templateValue(form, template, "valaistus");
  const tempo = templateValue(form, template, "tempo");
  const duration = templateValue(form, template, "kesto");
  const locked = templateValue(form, template, "lukitut");
  const primaryInput = getPrimaryUserInput(form, ["aloitus", "tapahtuma", "ymparisto", "lukitut"], true);
  const startDescription = finnishVisualValue(start || primaryInput);
  const sceneDescription = finnishVisualValue(action || primaryInput);
  const movementDescription = finnishVisualValue(subjectMove);
  const environmentDescription = finnishVisualValue(environment);
  const lockedDescription = finnishVisualValue(locked);

  const sentences = [
    `Luo erittäin realistinen ${finnishVisualValue(duration) || "lyhyt"} ${lowercaseFirst(finnishVisualValue(videoType) || "AI-videoklippi")}.`,
    aspectSentence(aspect, "video", "fi"),
    attachedImageSentence(attachedImage, "video", "fi"),
    environmentDescription ? `Ympäristö on ${environmentDescription}.` : "",
    startDescription ? `Videon alussa ${startDescription}.` : "Videon alussa tilanne on rauhallinen, uskottava ja arkinen.",
    sceneDescription ? `Videon aikana ${sceneDescription}.` : "Videon aikana tapahtuu vain pieni ja luonnollinen muutos.",
    movementDescription ? `Kohteen liike: ${movementDescription}.` : "Pidä kohteen liike pienenä, uskottavana ja arkisena.",
    camera ? `Käytä kameran liikkeenä: ${finnishVisualValue(camera)}.` : "Käytä kevyttä handheld-kameran liikettä tai muuta rauhallista kameraliikettä.",
    expression ? `Ilme ja tunne: ${finnishVisualValue(expression)}.` : "Ilmeen tulee olla luonnollinen, rauhallinen ja uskottava.",
    lighting ? `Valaistus: ${finnishVisualValue(lighting)}.` : "Käytä realistista, johdonmukaista valaistusta.",
    tempo ? `Tempo: ${finnishVisualValue(tempo)}.` : "Pidä rytmi rauhallisena ja todentuntuisena.",
    lockedDescription ? `Säilytä muuttumattomina: ${lockedDescription}.` : "Säilytä henkilön kasvot, vaatetus, tuote ja ympäristö johdonmukaisina.",
    tool ? videoToolInstruction(tool, aspect, attachedImage, "fi") : "",
    `Vältä ${finnishVisualValue(joinNatural([...template.negatiivisetOhjeet, templateValue(form, template, "negatiiviset")])) || "flickeröintiä, morphingia, kasvojen vääristymistä, ylimääräisiä raajoja, identiteetin muuttumista ja epärealistista liikettä"}.`,
  ].filter(Boolean);

  return appendFinnishVisualGuidance(sentences.join(" "));
};

const promptGoals = {
  "caption-ja-hashtagit": "Kirjoita valmis kuvateksti, 3 vaihtoehtoista aloitusta, 3 CTA-vaihtoehtoa ja hashtagit.",
  sisaltoideat: "Luo sisältöideat selkeässä muodossa: idea, kategoria, koukku eli pysäyttävä aloitus, formaatti, miksi idea toimii ja toteutusvinkki.",
  "ugc-videoprompti": "Kirjoita UGC-videon käsikirjoitus, B-roll-ideat, koukut, ruututekstit ja CTA luonnollisella, ei-mainosmaisella sävyllä.",
  mainosteksti: "Kirjoita luonnollinen ja konvertoiva mainosteksti, joka ei kuulosta liian mainosmaiselta tai geneeriseltä.",
  sahkoposti: "Kirjoita valmis sähköpostiviesti luonnollisella, ystävällisellä ja selkeällä sävyllä.",
  "myyntisivun-tekstiosio": "Kirjoita myyntisivun tekstiosio, joka on selkeä, konkreettinen, luotettava ja ei liian aggressiivinen.",
  "suno-musiikkipromptit": "Anna erikseen Suno Style -kenttä, Lyrics-kenttä tarvittaessa ja lyhyt kuvaus musiikin tunnelmasta.",
  aanitehostepromptit: "Kirjoita lyhyt ja selkeä äänitehosteen luomiseen sopiva prompti.",
  "some-analytiikka": "Tee analyysi, jossa on tärkeimmät havainnot, mahdolliset syyt, mitä kannattaa jatkaa, mitä kannattaa lopettaa ja 3–5 seuraavaa toimenpidettä.",
};

const buildGenericPrompt = (form, template) => {
  const details = naturalDetails(form, template);
  const toolGuidance = textToolGuidance(aiTool(form, template));
  const structure = Array.isArray(template.promptirunko.rakenne) ? template.promptirunko.rakenne.join(", ") : template.promptirunko.rakenne;
  const extraAvoid = templateValue(form, template, "valttavat") || templateValue(form, template, "negatiiviset");
  const restrictions = [...template.negatiivisetOhjeet, extraAvoid].filter(Boolean);

  return `Rooli:
${template.promptirunko.rooli}

Tavoite:
${promptGoals[template.id] || template.promptirunko.formaatti}

Käyttäjän antamat tiedot:
${details || "Tee maltilliset oletukset ja pidä lopputulos helposti muokattavana."}

${toolGuidance ? `Työkalukohtaiset ohjeet:\n${toolGuidance}\n` : ""}

Tyyli ja laatu:
${naturalInstructionText(template.realismiJaLuonnollisuusohjeet)}

Rakenne:
Sisällytä lopputulokseen nämä osat luonnollisessa järjestyksessä: ${structure}.

Formaatti:
${template.promptirunko.formaatti}

Rajoitteet:
${naturalInstructionText(restrictions)}

Lopputulos:
Tuota valmis sisältö suomeksi, ellei käyttäjän tiedoissa pyydetä muuta kieltä. Tee lopputuloksesta konkreettinen, luonnollinen, realistinen ja suoraan käytettävä. Älä lisää tyhjiä kohtia tai hakasulkeita.`;
};

export const buildPromptForTemplate = (form, template) => {
  if (template.id === "lyhytvideo-kasikirjoitus") return buildShortVideoPrompt(form, template);
  if (template.id === "ai-kuvapromptit-master") return buildDirectImagePrompt(form, template);
  if (template.id === "ai-videopromptit-master") return buildDirectVideoPrompt(form, template);
  return buildGenericPrompt(form, template);
};

export const buildShortVideoPrompt = (form, template) => {
  const toolGuidance = textToolGuidance(aiTool(form, template));
  const aihe = templateValue(form, template, "aihe");
  const tavoite = templateValue(form, template, "tavoite");
  const kanava = templateValue(form, template, "kanava");
  const pituus = templateValue(form, template, "pituus");
  const kohderyhma = templateValue(form, template, "kohderyhma");
  const tyyli = templateValue(form, template, "tyyli");
  const puhuttelumuoto = templateValue(form, template, "puhuttelumuoto");
  const ctaTyyppi = templateValue(form, template, "ctaTyyppi");
  const valttavat = templateValue(form, template, "valttavat");
  const erityishuomiot = templateValue(form, template, "erityishuomiot");

  const contextLines = [
    sentence(aihe, `Video käsittelee aihetta: ${aihe}.`),
    sentence(kanava, `Video tehdään ensisijaisesti kanavaan: ${kanava}.`),
    sentence(pituus, `Tavoitepituus on ${pituus}.`),
    sentence(kohderyhma, `Kohderyhmä on ${kohderyhma}.`),
    sentence(tavoite, `Videon päätavoite on ${tavoite}.`),
    sentence(ctaTyyppi, `CTA:n tyyppi on ${ctaTyyppi}.`),
    sentence(erityishuomiot, `Huomioi erityisesti: ${erityishuomiot}.`),
  ];

  const toneLines = [
    `Kirjoita käsikirjoitus luonnollisella puhekielellä niin, että se on helppo sanoa ääneen kameralle.`,
    sentence(tyyli, `Sävy saa olla ${tyyli}.`),
    sentence(puhuttelumuoto, `Käytä ${puhuttelumuoto}a.`),
    `Pidä ilmaisu lämpimänä, selkeänä, rohkaisevana, käytännönläheisenä ja ihmisen kuuloisena.`,
  ];

  const negativeLines = [
    ...template.negatiivisetOhjeet,
    valttavat,
  ];

  return `Rooli:
${template.promptirunko.rooli}

Tavoite:
Luo lyhytvideon käsikirjoitus, joka pysäyttää selauksen nopeasti, pitää katsojan mukana ja ohjaa hänet luontevasti seuraavaan askeleeseen.

Konteksti:
${contextLines.filter(Boolean).join("\n") || "Tee käsikirjoituksesta selkeä ja käyttökelpoinen lyhytvideoon. Jos jokin olennainen tieto puuttuu, tee maltillinen oletus ja pidä lopputulos helposti muokattavana."}

Tyyli ja sävy:
${toneLines.filter(Boolean).join("\n")}

Realismi- ja luonnollisuusohjeet:
${list(template.realismiJaLuonnollisuusohjeet)}

Rakenne:
1. Koukku 0–3 s: pysäytä selaus heti. Koukku tarkoittaa aloitusta, joka saa katsojan pysähtymään. Käytä esimerkiksi samaistuttavaa ongelmaa, pakottavaa kysymystä, vastaintuitiivista väitettä tai konkreettista tulosta.
2. Kipupiste tai ongelma 3–8 s: näytä tilanne konkreettisesti, ei abstraktina väitteenä.
3. Ratkaisu tai arvolupaus 8–15 s: kerro nopeasti, mikä helpottaa ongelmaa ja miksi katsojan kannattaa jatkaa.
4. Tarinan kehitys tai vaiheittainen selitys: anna 2–3 käytännön askelta, esimerkkiä tai havaintoa.
5. CTA viimeiset 3 s: tee toimintakehotuksesta selkeä, luonteva ja tavoitteeseen sopiva.

Formaatti:
Anna vastaus tässä muodossa:
- Valittu koukkutyyppi
- Käsikirjoitus kohtauksittain aikaleimoilla
- Kameralle puhuttavat repliikit
- Ruudulle tulevat tekstit
- Kuvaus- tai B-roll-vinkit
- 3 vaihtoehtoista koukkua eli pysäyttävää aloitusta
- 1 lyhyt kuvateksti
- 1 selkeä CTA

Rajoitteet:
${list(negativeLines)}

${toolGuidance ? `Työkalukohtaiset ohjeet:\n${toolGuidance}\n` : ""}

Lopputulos:
Kirjoita valmis käsikirjoitus suomeksi. Tee siitä konkreettinen, kuvauskelpoinen ja aloittelijalle helppo toteuttaa. Lopputuloksen pitää kuulostaa aidolta ihmiseltä, ei mainokselta eikä tekoälyn kirjoittamalta.`;
};